import { type MediaItem } from '@/types/media.type'
import { uploadFile, deleteFile } from '@/lib/firebase'
import { type FileUpload } from '@/components/share-components/multiple-media-upload'

export interface MediaImages {
  gallery: FileUpload[]
}

export interface HeaderImage {
  url: File | string
  fileName: string
}

export interface ImagesWithHeader {
  header: HeaderImage | null
  gallery: FileUpload[]
}

export interface UploadedImages {
  imageHeaderUrl?: string
  mediaUrls: MediaItem[]
}

/**
 * Upload header image
 */
export async function uploadHeaderImage(
  headerImage: HeaderImage | null,
  folderPath: string,
): Promise<string | undefined> {
  if (!headerImage) {
    console.log('No header image to upload')
    return undefined
  }

  console.log('Processing header image:', headerImage)

  if (typeof headerImage.url === 'string') {
    console.log('Header image already uploaded:', headerImage.url)
    return headerImage.url
  }

  console.log('Uploading new header image:', headerImage.fileName)
  const url = await uploadFile(
    headerImage.url,
    `${folderPath}/headers`,
    headerImage.fileName,
  )
  console.log('Header image uploaded successfully:', url)
  return url
}

/**
 * Upload gallery images
 */
export async function uploadGalleryImages(
  galleryImages: FileUpload[],
  folderPath: string,
): Promise<MediaItem[]> {
  if (!galleryImages || galleryImages.length === 0) {
    console.log('No gallery images to upload')
    return []
  }

  console.log('Processing gallery images:', galleryImages.length, 'files')

  const processedUrls = new Set<string>()

  const uploadPromises = galleryImages.map(async (image, index) => {
    const imageKey =
      typeof image.url === 'string' ? image.url : `${image.fileName}-${index}`
    if (processedUrls.has(imageKey)) {
      console.log('Skipping duplicate image:', imageKey)
      return null
    }
    processedUrls.add(imageKey)

    if (typeof image.url === 'string') {
      return {
        name: image.fileName,
        size: image.size || 0,
        type: image.type,
        url: image.url,
      }
    } else {
      try {
        const url = await uploadFile(
          image.url,
          `${folderPath}/gallery`,
          image.fileName,
        )
        console.log('Gallery image uploaded successfully:', image.fileName, url)
        return {
          name: image.fileName,
          size: image.size || image.url.size,
          type: image.type,
          url: url,
        }
      } catch (error) {
        console.error('Error uploading gallery file:', image.fileName, error)
        throw error
      }
    }
  })

  const results = await Promise.all(uploadPromises)
  const validResults = results.filter(
    (result): result is MediaItem => result !== null,
  )
  console.log(
    'Gallery upload results:',
    validResults.length,
    'successful uploads',
  )
  return validResults
}

/**
 * Upload images with header and gallery
 */
export async function uploadImagesWithHeader(
  images: ImagesWithHeader,
  folderPath: string,
): Promise<UploadedImages> {
  const uploadedImages: UploadedImages = { mediaUrls: [] }

  // Upload header image
  const headerUrl = await uploadHeaderImage(images.header, folderPath)
  if (headerUrl) {
    uploadedImages.imageHeaderUrl = headerUrl
  }

  // Upload gallery images (always include mediaUrls, even when empty)
  const galleryMediaItems = await uploadGalleryImages(
    images.gallery,
    folderPath,
  )
  uploadedImages.mediaUrls = galleryMediaItems

  console.log('Uploaded images summary:', {
    hasHeaderImage: !!uploadedImages.imageHeaderUrl,
    galleryCount: uploadedImages.mediaUrls.length,
  })

  return uploadedImages
}

/**
 * Upload gallery images only
 */
export async function uploadGalleryImagesOnly(
  images: MediaImages,
  folderPath: string,
): Promise<UploadedImages> {
  const uploadedImages: UploadedImages = { mediaUrls: [] }

  // Upload gallery images (always include mediaUrls, even when empty)
  const galleryMediaItems = await uploadGalleryImages(
    images.gallery,
    folderPath,
  )
  uploadedImages.mediaUrls = galleryMediaItems

  console.log('Uploaded images summary:', {
    galleryCount: uploadedImages.mediaUrls.length,
  })

  return uploadedImages
}

/**
 * Delete media files that were marked for deletion
 */
export async function deleteMarkedMediaFiles(
  deletedMediaUrls: string[],
): Promise<void> {
  if (deletedMediaUrls.length === 0) {
    return
  }

  console.log('Deleting marked media files:', deletedMediaUrls)
  try {
    const deletePromises = deletedMediaUrls.map(async (url) => {
      try {
        await deleteFile(url)
        console.log('Successfully deleted media file:', url)
      } catch (error) {
        console.error('Error deleting media file:', url, error)
      }
    })
    await Promise.all(deletePromises)
    console.log('All marked media files processed')
  } catch (error) {
    console.error('Error processing media file deletions:', error)
  }
}
